import { useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_INDIVIDUAL_FORM = {
  name: "",
  email: "",
  phone: "",
  athlete_name: "",
  athlete_age: "",
  sport: "",
  message: ""
};

const INITIAL_ORG_FORM = {
  name: "",
  email: "",
  phone: "",
  organization_name: "",
  organization_sport: "",
  number_of_athletes: "",
  message: ""
};

/**
 * Validates contact form data based on contact type
 * @param {string} contactType - "individual" or "organization"
 * @param {object} individualForm - Individual form data
 * @param {object} orgForm - Organization form data
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export const validateContactForm = (contactType, individualForm, orgForm) => {
  if (contactType === "individual") {
    if (!individualForm.name || !individualForm.email || !individualForm.message) {
      return { isValid: false, error: "Please fill in all required fields" };
    }
  } else {
    if (!orgForm.name || !orgForm.email || !orgForm.organization_name || !orgForm.message) {
      return { isValid: false, error: "Please fill in all required fields" };
    }
  }
  return { isValid: true, error: null };
};

/**
 * Builds the payload for contact form submission
 * @param {string} contactType - "individual" or "organization"
 * @param {object} individualForm - Individual form data
 * @param {object} orgForm - Organization form data
 * @returns {object} - API payload
 */
export const buildContactPayload = (contactType, individualForm, orgForm) => {
  if (contactType === "individual") {
    return {
      ...individualForm,
      contact_type: "individual",
      athlete_age: individualForm.athlete_age ? parseInt(individualForm.athlete_age, 10) : null
    };
  }
  return {
    name: orgForm.name,
    email: orgForm.email,
    phone: orgForm.phone,
    message: orgForm.message,
    contact_type: "organization",
    organization_name: orgForm.organization_name,
    organization_sport: orgForm.organization_sport,
    number_of_athletes: orgForm.number_of_athletes ? parseInt(orgForm.number_of_athletes, 10) : null
  };
};

/**
 * Custom hook for contact form state and submission
 */
export const useContactForm = () => {
  const [contactType, setContactType] = useState("individual");
  const [individualForm, setIndividualForm] = useState(INITIAL_INDIVIDUAL_FORM);
  const [orgForm, setOrgForm] = useState(INITIAL_ORG_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleIndividualChange = useCallback((e) => {
    const { name, value } = e.target;
    setIndividualForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleOrgChange = useCallback((e) => {
    const { name, value } = e.target;
    setOrgForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetForms = useCallback(() => {
    setSubmitted(false);
    setIndividualForm(INITIAL_INDIVIDUAL_FORM);
    setOrgForm(INITIAL_ORG_FORM);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const validation = validateContactForm(contactType, individualForm, orgForm);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    setLoading(true);
    try {
      const payload = buildContactPayload(contactType, individualForm, orgForm);
      const response = await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      
      // Check if email was actually sent
      if (response.data.email_sent) {
        toast.success("Message sent successfully!");
      } else {
        toast.success("Message received! We'll be in touch soon.");
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [contactType, individualForm, orgForm]);

  return {
    contactType,
    setContactType,
    individualForm,
    orgForm,
    loading,
    submitted,
    handleIndividualChange,
    handleOrgChange,
    handleSubmit,
    resetForms
  };
};
